import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness'
import * as Sentry from '@sentry/react'
import { type ComponentProps, useEffect, useMemo, useRef, useState } from 'react'
import { useSessionId } from '@/hooks/useSessionId'
import type { Translations } from '@/types/translations'
import translations from '../../../public/i18n.json'

type LivenessVerificationSession = {
  id: string
  providerSessionId: string
}

type CreateLivenessSessionResponse = {
  livenessVerificationSession: LivenessVerificationSession
  // UNIX timestamp (ms) returned by backend; required for clock skew handling.
  serverDate: number
}

type LivenessMessage = {
  type: 'CAPTURED'
}

/**
 * Legacy message format used by older SDK clients (expo-sdk / react-sdk) that
 * only listen for 'SUCCESS' and 'ERROR' message types. We keep sending these
 * alongside 'CAPTURED' so older clients don't hang. Once all clients have
 * migrated to the new SDK, legacy messages can be removed.
 */
type LegacyLivenessMessage = {
  type: 'SUCCESS' | 'ERROR'
  data?: {
    error?: {
      message: string
      state?: string
    }
  }
}

type FaceLivenessProps = {
  onComplete: () => void
}

function useLanguageFromQuery(): string {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const language = params.get('language') || params.get('lang') || 'en'

    // Validate that the language is supported
    const supportedLanguages = ['en', 'pt']
    return supportedLanguages.includes(language) ? language : 'en'
  }, [])
}

// Helper function to get translation with fallback to English
function getTranslation(translationObject: any, language: string): string {
  return translationObject[language] || translationObject.en || ''
}

// Assert the translations type to avoid TypeScript errors
const livenessTranslations = (translations as Translations).liveness

function getSystemClockOffset(serverTime?: number) {
  if (typeof serverTime !== 'number') return undefined

  const maxSupportedClockSkew = 5 * 60 * 1000 // 5 minutes
  const delta = serverTime - Date.now()

  Sentry.captureMessage('System clock offset', {
    extra: {
      serverTime,
      delta,
      maxSupportedClockSkew,
    },
  })

  return Math.abs(delta) >= maxSupportedClockSkew ? delta : undefined
}

export function LivenessStep({ onComplete }: FaceLivenessProps) {
  const sessionId = useSessionId()
  const language = useLanguageFromQuery()
  const [livenessSession, setLivenessSession] =
    useState<LivenessVerificationSession | null>(null)
  const [systemClockOffset, setSystemClockOffset] = useState<
    number | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const sessionCreationInitiated = useRef(false)

  const handleAnalysisComplete = async () => {
    try {
      if (!livenessSession) return

      await fetch(
        `${import.meta.env.VITE_NOTUS_API_URL}/kyc/individual-verification-sessions/liveness/${livenessSession.id}/process`,
        {
          method: 'POST',
        },
      )

      postMessageToApp({ type: 'CAPTURED' }, { type: 'SUCCESS' })
      onComplete()
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          sessionId,
          livenessSessionId: livenessSession?.id,
        },
      })
      // Old code threw here (no postMessage), but we now always complete
      postMessageToApp({ type: 'CAPTURED' }, { type: 'SUCCESS' })
      onComplete()
    }
  }

  /**
   * Posts a message to the parent app (React Native WebView or iframe parent).
   *
   * We always send the new 'CAPTURED' message. When a legacyMessage is provided,
   * it is also sent so that older SDK clients (expo-sdk / react-sdk) — which
   * only listen for 'SUCCESS' and 'ERROR' — still behave correctly. New SDK
   * clients listen for 'CAPTURED' and safely ignore the legacy messages. Once
   * all clients have migrated to the new SDK, the legacy messages can be removed.
   */
  function postMessageToApp(
    message: LivenessMessage,
    legacyMessage?: LegacyLivenessMessage,
  ) {
    if (window?.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message))
      if (legacyMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify(legacyMessage))
      }
    } else {
      window.top?.postMessage(message, '*')
      if (legacyMessage) {
        window.top?.postMessage(legacyMessage, '*')
      }
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: postMessageToApp changes on every render
  useEffect(() => {
    if (sessionCreationInitiated.current) {
      return
    }
    sessionCreationInitiated.current = true

    const createLivenessSession = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_NOTUS_API_URL}/kyc/individual-verification-sessions/liveness`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              parentSessionId: sessionId,
            }),
          },
        )

        const data = await response.json()
        const typedData = data as CreateLivenessSessionResponse

        if (typedData?.serverDate === undefined) {
          throw new Error('Missing serverDate from liveness response')
        }

        if (!response.ok) {
          if (!data.message) {
            const error = new Error(
              getTranslation(livenessTranslations.error.unknown, language),
            )
            Sentry.captureException(error, {
              extra: {
                sessionId,
                status: response.status,
                responseBody: data,
              },
            })
            // Old code threw here (no postMessage), but we now always complete
            postMessageToApp({ type: 'CAPTURED' }, { type: 'SUCCESS' })
            onComplete()
            return
          }

          Sentry.captureException(new Error(data.message), {
            extra: {
              sessionId,
              status: response.status,
              responseBody: data,
            },
          })

          postMessageToApp({ type: 'CAPTURED' }, {
            type: 'ERROR',
            data: { error: { message: data.message } },
          })
          onComplete()
          return
        }

        setLivenessSession(
          typedData.livenessVerificationSession as LivenessVerificationSession,
        )

        const offset = getSystemClockOffset(typedData.serverDate)
        setSystemClockOffset(offset)
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            sessionId,
          },
        })
        postMessageToApp({ type: 'CAPTURED' }, {
          type: 'ERROR',
          data: {
            error: {
              message: getTranslation(
                livenessTranslations.error.initialization,
                language,
              ),
            },
          },
        })
        onComplete()
      } finally {
        setIsLoading(false)
      }
    }

    createLivenessSession()
  }, [sessionId])

  if (isLoading || !livenessSession) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-(--kyc-bg) p-6">
        <div className="flex flex-col items-center gap-4 max-w-sm w-full text-center">
          {/* Loading spinner */}
          <div className="relative w-16 h-16 mb-2">
            <div className="absolute inset-0 border-4 border-(--kyc-disabled) rounded-full"></div>
            <div className="absolute inset-0 border-4 border-t-transparent border-(--kyc-primary) rounded-full animate-spin"></div>
          </div>

          {/* Content */}
          <div>
            <h2 className="text-xl font-semibold text-(--kyc-text) mb-2">
              {getTranslation(livenessTranslations.loading, language)}
            </h2>
            <p className="text-(--kyc-disabled-text) text-sm leading-relaxed">
              {getTranslation(livenessTranslations.loadingMessage, language)}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const { displayText } = livenessTranslations
  const faceLivenessConfig =
    systemClockOffset !== undefined
      ? ({
          systemClockOffset,
        } as ComponentProps<typeof FaceLivenessDetector>['config'])
      : undefined

  return (
    <FaceLivenessDetector
      sessionId={livenessSession.providerSessionId}
      region="us-east-1"
      onAnalysisComplete={handleAnalysisComplete}
      config={faceLivenessConfig}
      onError={(error) => {
        Sentry.captureException(error, {
          extra: {
            sessionId,
            state: error.state,
            livenessSessionId: livenessSession?.id,
          },
        })
        try {
          const { Message } = JSON.parse(error.error.message) as {
            Message: string
          }
          postMessageToApp({ type: 'CAPTURED' }, {
            type: 'ERROR',
            data: { error: { message: Message, state: error.state } },
          })
        } catch {
          postMessageToApp({ type: 'CAPTURED' }, {
            type: 'ERROR',
            data: {
              error: {
                message: getTranslation(
                  livenessTranslations.errors.unknown,
                  language,
                ),
              },
            },
          })
        }
        onComplete()
      }}
      disableStartScreen={true}
      displayText={{
        errorLabelText: getTranslation(displayText.errorLabel, language),
        connectionTimeoutHeaderText: getTranslation(
          displayText.connectionTimeout.header,
          language,
        ),
        connectionTimeoutMessageText: getTranslation(
          displayText.connectionTimeout.message,
          language,
        ),
        timeoutHeaderText: getTranslation(displayText.timeout.header, language),
        timeoutMessageText: getTranslation(
          displayText.timeout.message,
          language,
        ),
        faceDistanceHeaderText: getTranslation(
          displayText.faceDistance.header,
          language,
        ),
        faceDistanceMessageText: getTranslation(
          displayText.faceDistance.message,
          language,
        ),
        multipleFacesHeaderText: getTranslation(
          displayText.multipleFaces.header,
          language,
        ),
        multipleFacesMessageText: getTranslation(
          displayText.multipleFaces.message,
          language,
        ),
        clientHeaderText: getTranslation(displayText.client.header, language),
        clientMessageText: getTranslation(displayText.client.message, language),
        serverHeaderText: getTranslation(displayText.server.header, language),
        serverMessageText: getTranslation(displayText.server.message, language),
        landscapeHeaderText: getTranslation(
          displayText.landscape.header,
          language,
        ),
        landscapeMessageText: getTranslation(
          displayText.landscape.message,
          language,
        ),
        portraitMessageText: getTranslation(
          displayText.landscape.portrait,
          language,
        ),
        tryAgainText: getTranslation(displayText.tryAgain, language),
        cameraMinSpecificationsHeadingText: getTranslation(
          displayText.camera.minSpecs.heading,
          language,
        ),
        cameraMinSpecificationsMessageText: getTranslation(
          displayText.camera.minSpecs.message,
          language,
        ),
        cameraNotFoundHeadingText: getTranslation(
          displayText.camera.notFound.heading,
          language,
        ),
        cameraNotFoundMessageText: getTranslation(
          displayText.camera.notFound.message,
          language,
        ),
        a11yVideoLabelText: getTranslation(
          displayText.accessibility.videoLabel,
          language,
        ),
        cancelLivenessCheckText: getTranslation(
          displayText.accessibility.cancelCheck,
          language,
        ),
        goodFitCaptionText: getTranslation(
          displayText.fit.good.caption,
          language,
        ),
        goodFitAltText: getTranslation(displayText.fit.good.alt, language),
        hintCenterFaceText: getTranslation(
          displayText.hints.centerFace,
          language,
        ),
        hintCenterFaceInstructionText: getTranslation(
          displayText.hints.centerFaceInstruction,
          language,
        ),
        hintFaceOffCenterText: getTranslation(
          displayText.hints.faceOffCenter,
          language,
        ),
        hintMoveFaceFrontOfCameraText: getTranslation(
          displayText.hints.moveFaceFront,
          language,
        ),
        hintTooManyFacesText: getTranslation(
          displayText.hints.tooManyFaces,
          language,
        ),
        hintFaceDetectedText: getTranslation(
          displayText.hints.faceDetected,
          language,
        ),
        hintCanNotIdentifyText: getTranslation(
          displayText.hints.cannotIdentify,
          language,
        ),
        hintTooCloseText: getTranslation(displayText.hints.tooClose, language),
        hintTooFarText: getTranslation(displayText.hints.tooFar, language),
        hintConnectingText: getTranslation(
          displayText.hints.connecting,
          language,
        ),
        hintVerifyingText: getTranslation(
          displayText.hints.verifying,
          language,
        ),
        hintCheckCompleteText: getTranslation(
          displayText.hints.checkComplete,
          language,
        ),
        hintIlluminationTooBrightText: getTranslation(
          displayText.hints.illumination.tooBright,
          language,
        ),
        hintIlluminationTooDarkText: getTranslation(
          displayText.hints.illumination.tooDark,
          language,
        ),
        hintIlluminationNormalText: getTranslation(
          displayText.hints.illumination.normal,
          language,
        ),
        hintHoldFaceForFreshnessText: getTranslation(
          displayText.hints.holdFace,
          language,
        ),
        hintMatchIndicatorText: getTranslation(
          displayText.hints.matchIndicator,
          language,
        ),
        photosensitivityWarningBodyText: getTranslation(
          displayText.photosensitivity.warning.body,
          language,
        ),
        photosensitivityWarningHeadingText: getTranslation(
          displayText.photosensitivity.warning.heading,
          language,
        ),
        photosensitivityWarningInfoText: getTranslation(
          displayText.photosensitivity.warning.info,
          language,
        ),
        photosensitivityWarningLabelText: getTranslation(
          displayText.photosensitivity.warning.label,
          language,
        ),
        retryCameraPermissionsText: getTranslation(
          displayText.camera.permissions.retry,
          language,
        ),
        waitingCameraPermissionText: getTranslation(
          displayText.camera.permissions.waiting,
          language,
        ),
        startScreenBeginCheckText: getTranslation(
          displayText.startScreen.beginCheck,
          language,
        ),
        tooFarCaptionText: getTranslation(
          displayText.fit.tooFar.caption,
          language,
        ),
        tooFarAltText: getTranslation(displayText.fit.tooFar.alt, language),
      }}
      onUserCancel={() => {
        Sentry.captureMessage('User cancelled liveness check', {
          level: 'info',
          extra: {
            sessionId,
            livenessSessionId: livenessSession?.id,
          },
        })
        postMessageToApp({ type: 'CAPTURED' }, {
          type: 'ERROR',
          data: {
            error: {
              message: getTranslation(
                livenessTranslations.errors.userCancelled,
                language,
              ),
            },
          },
        })
        onComplete()
      }}
      components={{
        ErrorView: () => null,
      }}
    />
  )
}
